import { useEffect } from 'react';
import { Box, Text } from '@rsos/base-components';
import { Loader } from '@rsos/base-components';
import { Accordion } from '@rsos/components/capstone/base';
import { trackDisplayStatusUpdate } from '../../../gainsightTrackings/airAmbulanceTrackings';
import { FullWidthContent } from '../../JurisdictionView/EventPane/tabs/OverviewTab/OverviewTab.styles';
import { useExistingRequest } from '../AirAmbulanceForm/hooks/useExistingRequest';

const formatTime = timestamp => {
  if (!timestamp) return 'N/A';
  try {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (error) {
    // Error formatting timestamp - return fallback
    return 'N/A';
  }
};

/**
 * AirAmbulanceStatusUpdates component renders status updates for air ambulance requests.
 */
const AirAmbulanceStatusUpdates = () => {
  const {
    existingRequest,
    latestStatus,
    remainingStatuses = [],
  } = useExistingRequest();

  const requestId =
    existingRequest?.body?.ext_trip_id || existingRequest?.request_id;
  const statusDisplay = latestStatus?.status?.display;

  // Track the latest status update only when requestId or status changes
  useEffect(() => {
    if (statusDisplay && requestId) {
      trackDisplayStatusUpdate({
        requestId,
        status: statusDisplay,
      });
    }
  }, [requestId, statusDisplay]);

  const renderHeader = () => (
    <Box>
      <Text mb={2} color="primaryText" fontWeight="bold" size="normal">
        Status Updates
      </Text>
      <Box horizontal>
        {latestStatus?.status ? (
          <>
            <Text color="primaryBase" size="normal">
              {formatTime(latestStatus?.status?.statusTimeUTC)}
            </Text>
            <Text ml={2} color="secondaryText" size="normal">
              {latestStatus?.status?.display || 'No status'}
            </Text>
          </>
        ) : (
          <Loader noText />
        )}
      </Box>
    </Box>
  );

  const renderContent = () => (
    <>
      {Array.isArray(remainingStatuses) &&
        // There can be blank statuses. If this is the case, we omit them entirely.
        remainingStatuses
          .filter(
            status => status?.status?.display && status?.status?.statusTimeUTC,
          )
          .map((status, index) => (
            <Box
              key={`${status.status.statusTimeUTC || index}-${index}`}
              horizontal
            >
              <Text color="primaryBase" size="normal">
                {formatTime(status?.status?.statusTimeUTC)}
              </Text>
              <Text ml={2} color="secondaryText" size="normal">
                {status.status.display}
              </Text>
            </Box>
          ))}
    </>
  );

  return (
    <>
      <Box m={1} mt={2} mb={2}>
        <FullWidthContent>
          <Box>
            <Box horizontal justify="space-between">
              <Box width="100%">
                {remainingStatuses.length > 0 ? (
                  <Accordion
                    header={renderHeader()}
                    headerIcon="chevron"
                    interactionType="button"
                    noBorder
                    width="100%"
                    headerPadding="0"
                    padding="0"
                    content={renderContent()}
                  />
                ) : (
                  renderHeader()
                )}
              </Box>
            </Box>
          </Box>
        </FullWidthContent>
      </Box>
    </>
  );
};

export default AirAmbulanceStatusUpdates;
